import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause, Volume2, VolumeX } from "lucide-react";
import { api, BACKEND_URL } from "@/lib/api";

/**
 * Cinematic founder video with poster fallback.
 * If Toriano has uploaded a self-hosted video, it plays that.
 * Otherwise falls back to the Mixkit stock loop, and if that fails, the poster.
 */
const DEFAULT_STOCK =
  "https://assets.mixkit.co/videos/preview/mixkit-german-shepherd-dog-running-in-a-field-1620-large.mp4";
const DEFAULT_POSTER =
  "https://customer-assets-v7afamib.emergentagent.net/job_packleader-hub/artifacts/0f7iuk2g_Trailblazer.jpeg";

export default function FounderVideo({
  caption = "The Founder's Story · A Cinematic Introduction",
  testid = "founder-video",
  poster,
  src,
}) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [muted, setMuted] = useState(true);
  const [failed, setFailed] = useState(false);
  const [remote, setRemote] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/media/founder-video").then((r) => {
      if (!cancelled && r.data?.url) {
        setRemote({
          url: r.data.url.startsWith("http") ? r.data.url : `${BACKEND_URL}${r.data.url}`,
          poster_url: r.data.poster_url,
          caption: r.data.caption,
        });
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const videoSrc = src || remote?.url || DEFAULT_STOCK;
  const videoPoster = poster || remote?.poster_url || DEFAULT_POSTER;
  const videoCaption = remote?.caption || caption;

  const toggle = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) { v.play(); setPlaying(true); }
    else { v.pause(); setPlaying(false); }
  };
  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };

  return (
    <div data-testid={testid} className="relative overflow-hidden rounded-3xl aspect-video group">
      {!failed && (
        <video
          key={videoSrc}
          ref={videoRef}
          className="absolute inset-0 w-full h-full object-cover"
          src={videoSrc}
          poster={videoPoster}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          onError={() => setFailed(true)}
        />
      )}
      {failed && (
        <img src={videoPoster} alt="" className="absolute inset-0 w-full h-full object-cover kenburns" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-black/30 pointer-events-none" />

      {remote?.url && (
        <div className="absolute top-4 right-4 overline text-[10px] text-[color:var(--wk-gold)] glass px-3 py-1 rounded-full">
          Uploaded by the Founder
        </div>
      )}

      <div className="absolute inset-0 flex items-end p-6 md:p-10">
        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid={`${testid}-play`}
            aria-label={playing ? "Pause" : "Play"}
            className="w-14 h-14 rounded-full bg-[color:var(--wk-gold)] text-black flex items-center justify-center hover:scale-105 transition-transform"
          >
            {playing && !failed ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
          </button>
          {!failed && (
            <button
              onClick={toggleMute}
              data-testid={`${testid}-mute`}
              aria-label={muted ? "Unmute" : "Mute"}
              className="w-11 h-11 rounded-full glass border border-white/20 flex items-center justify-center text-white hover:border-[color:var(--wk-gold)] hover:text-[color:var(--wk-gold)] transition-colors"
            >
              {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
            </button>
          )}
          <motion.div
            initial={{ opacity: 0, x: -6 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="ml-2 hidden md:block"
          >
            <div className="overline text-[10px] text-[color:var(--wk-gold)]">Now Playing</div>
            <div className="font-serif-display text-lg text-white leading-tight">{videoCaption}</div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
